import Axios, { AxiosRequestConfig, CancelTokenSource } from 'axios';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { DANGEROUSLY_REQUEST_ALL_PAGES } from '~src/constants';
import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import { SortingValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { fetchVisibleSurveys, getSurvey } from '~src/modules/surveys/actions/surveyActions';
import { FilterTypes } from '~src/modules/surveys/constants/filterOptions';
import { FetchSurveysParams, Survey, SurveyExtended, SurveysListResp } from '~src/modules/surveys/interfaces';
import { StoreI } from '~src/rootReducer';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { getContentLocale } from '~src/services/session';
import { translateContent } from '~src/services/translationServices';
import { SvgIconName } from '~src/shared/SvgIcon';
import { OptionItem } from '~src/shared/selectors/DdSearchContent';
import SearchListSelector from '~src/shared/selectors/searchListSelector/SearchListSelector';

interface StateProps {
  contentLocale: string;
  loadingSurveys: boolean;
  surveys: SurveyExtended[];
}

interface DispatchProps {
  getSurvey: (id: string) => ApiReduxResp<Survey>;
  fetchVisibleSurveys(query: FetchSurveysParams, config?: AxiosRequestConfig): ApiReduxResp<SurveysListResp>;
}

interface OwnProps {
  triggerIconName: SvgIconName;
  filterSubject: FilterTypes;
  initialOpt: string | null;
  defaultOpt?: OptionItem;
  surveyType: SurveySubjectTypes;
  inactive?: boolean;
  onSelect(id: OptionItem): void;
}

type Props = OwnProps & StateProps & DispatchProps;

const SurveysSelector: FC<Props> = (props) => {
  const [t] = useTranslation();
  const [optionsList, setOptionsList] = useState<OptionItem[] | null>(null);
  const [initialOpt, setInitialOpt] = useState<OptionItem | undefined>(props.defaultOpt);
  const cancelTokenSource = useRef<CancelTokenSource | null>(null);
  const defaultQueryParams: FetchSurveysParams = {
    ['filter.type']: props.surveyType,
    filterSubject: props.filterSubject,
    sort: `${SortingValues.SurveyNames_asc}.${props.contentLocale}`,
    // LN-557 requires pagination to be done locally. So we need *all* the data.
    // Limiting to 10000 as a safe assumption that frontend can handle atmost this much
    ...DANGEROUSLY_REQUEST_ALL_PAGES,
  };
  const [filteredSurveys, setFilteredSurveys] = useState(props.surveys || []);

  useEffect(() => {
    if (!props.initialOpt) {
      setInitialOpt(props.defaultOpt);
    }
  }, [props.initialOpt]);

  useEffect(() => {
    void fetchSurveys(defaultQueryParams);
    return () => {
      if (cancelTokenSource.current) cancelTokenSource.current.cancel();
    };
  }, []);

  useEffect(() => setFilteredSurveys(props.surveys), [props.surveys]);

  useEffect(() => {
    updateOptsList();
  }, [filteredSurveys]);

  const fetchSurveys = async (params?: FetchSurveysParams | string | null): Promise<any> => {
    let mergedParams: FetchSurveysParams = {};

    if (typeof params === 'string' && !!params) {
      mergedParams = { filterName: params };
    } else if (!!params && typeof params === 'object') {
      mergedParams = params;
    }

    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel();
    }
    cancelTokenSource.current = Axios.CancelToken.source();

    return props.fetchVisibleSurveys(
      { ...defaultQueryParams, ...mergedParams },
      {
        cancelToken: cancelTokenSource.current.token,
      },
    );
  };

  const updateOptsList = () => {
    let initialOption: OptionItem | null = null;
    const surveys = filteredSurveys || [];
    const options = surveys.map((item) => {
      const mappedOpt = {
        id: item.id,
        name: translateContent(item.name),
      };
      if (
        props.initialOpt &&
        initialOpt &&
        initialOpt.id !== props.initialOpt &&
        item.id === props.initialOpt
      ) {
        initialOption = mappedOpt;
      }
      return mappedOpt;
    });
    if (!initialOption && props.initialOpt) {
      void props.getSurvey(props.initialOpt).then((res) => {
        const survey = res.response && res.response?.data; // this sometimes is an error response (ex duplicate request)
        if (survey) {
          setInitialOpt({ id: survey.id, name: translateContent(survey.name) });
        }
      });
    }
    if (initialOption) {
      setInitialOpt(initialOption);
    }
    setOptionsList(options);
  };

  const handleSearch = async (searchTerm = '', isPaginationRequest?: boolean) => {
    // We don't handle pagination. Search is done locally.
    if (isPaginationRequest) return;

    const newSurveys = props.surveys.filter((survey) =>
      translateContent(survey.name)?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    setFilteredSurveys(newSurveys);
  };

  return (
    <SearchListSelector
      initialOpt={initialOpt}
      defaultOpt={props.defaultOpt}
      triggerIconName={props.triggerIconName}
      placeholder={{
        key: 'x.compare.header.filters.select_survey.search_placeholder',
        value: t('x.compare.header.filters.select_survey.search_placeholder'),
      }}
      onSelect={props.onSelect}
      optionsList={optionsList}
      loadingSearch={props.loadingSurveys}
      fetchItems={handleSearch}
      nothingFoundKey="x.compare.header.filters.select_survey.nothing_found"
      noMoreItems={!filteredSurveys?.length}
      inactiveKey={`x.compare.header.filters.select_survey.${props.surveyType}.inactive`}
      inactive={props.inactive}
    />
  );
};

const mapState2Props = (state: StoreI, { surveyType }: OwnProps): StateProps => ({
  loadingSurveys: state.surveyManagement.surveys[surveyType].loading,
  surveys: state.surveyManagement.surveys[surveyType].items,
  contentLocale: getContentLocale(state),
});

const mapDispatch2Props = (dispatch: any, { surveyType }: OwnProps): DispatchProps => ({
  fetchVisibleSurveys: (query, config) =>
    // LN-557: Perform search operation on client side
    dispatch(fetchVisibleSurveys(query, surveyType, config)),

  getSurvey: (id) => dispatch(getSurvey(id)),
});

const SurveysSelectorC = connect<StateProps, DispatchProps, OwnProps, StoreI>(
  mapState2Props,
  mapDispatch2Props,
)(SurveysSelector);

export default SurveysSelectorC;
